'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Terminal, Copy, Check, AlertTriangle, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

// Real Supabase credentials (created in migration)
const DEMO_EMAIL = 'hunter@bloodhound.run';
const DEMO_PASSWORD = 'Bl00dH0und#2026';

interface LoginFormData {
  email: string;
  password: string;
}

interface ResetFormData {
  email: string;
}

function CredentialBox({
  onUse,
}: {
  onUse: (email: string, password: string) => void;
}) {
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const copy = (value: string, field: string) => {
    navigator.clipboard.writeText(value);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  return (
    <div className="mt-5 rounded-lg border border-terminal-green/20 bg-terminal-green/5 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Terminal size={12} className="text-terminal-green" />
        <span className="font-mono text-xs text-terminal-green tracking-widest uppercase">
          Demo Credentials
        </span>
      </div>
      <div className="space-y-2">
        {[
          { label: 'EMAIL', value: DEMO_EMAIL, field: 'email' },
          { label: 'PASS', value: DEMO_PASSWORD, field: 'password' },
        ].map(({ label, value, field }) => (
          <div key={`cred-${field}`} className="flex items-center justify-between gap-2">
            <span className="font-mono text-xs text-terminal-muted w-12">{label}</span>
            <span className="font-mono text-xs text-terminal-text flex-1 truncate">
              {field === 'password' ? '••••••••••••••••' : value}
            </span>
            <button
              type="button"
              onClick={() => copy(value, field)}
              className="text-terminal-muted hover:text-terminal-green transition-colors duration-150 p-1"
              aria-label={`Copy ${label}`}
            >
              {copiedField === field ? (
                <Check size={12} className="text-terminal-green" />
              ) : (
                <Copy size={12} />
              )}
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() => onUse(DEMO_EMAIL, DEMO_PASSWORD)}
        className="mt-3 w-full text-center font-mono text-xs text-terminal-green border border-terminal-green/30 rounded px-3 py-1.5 hover:bg-terminal-green/10 transition-all duration-150 active:scale-95"
      >
        AUTOFILL CREDENTIALS
      </button>
    </div>
  );
}

function ForgotPasswordForm({ onBack }: { onBack: () => void }) {
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const { register, handleSubmit, formState: { errors } } = useForm<ResetFormData>();
  const supabase = createClient();

  const onSubmit = async (data: ResetFormData) => {
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(data.email, {
        redirectTo: `${process.env.NEXT_PUBLIC_SITE_URL}/auth/callback?next=/reset-password`,
      });
      if (error) throw error;
      setSent(true);
      toast.success('Reset link sent — check your inbox.');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to send reset email.');
    } finally {
      setIsLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="bg-terminal-card border border-terminal-border rounded-xl p-6 terminal-glow text-center">
        <KeyRound size={32} className="text-terminal-green mx-auto mb-3" />
        <p className="font-mono text-sm text-terminal-text mb-1">RESET LINK SENT</p>
        <p className="font-mono text-xs text-terminal-muted mb-4">Check your email inbox and follow the link to reset your password.</p>
        <button type="button" onClick={onBack} className="font-mono text-xs text-terminal-green hover:underline">
          ← Back to login
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="bg-terminal-card border border-terminal-border rounded-xl p-6 terminal-glow">
      <div className="flex items-center gap-2 mb-4">
        <KeyRound size={14} className="text-terminal-green" />
        <span className="font-mono text-sm text-terminal-text">RESET PASSWORD</span>
      </div>
      <p className="font-mono text-xs text-terminal-muted mb-4">Enter your email and we'll send a reset link.</p>
      <div className="mb-4">
        <label htmlFor="reset-email" className="block font-mono text-xs text-terminal-muted uppercase tracking-widest mb-1.5">
          EMAIL ADDRESS
        </label>
        <input
          id="reset-email"
          type="email"
          placeholder="hunter@bloodhound.run"
          className={`w-full bg-terminal-bg border rounded-lg px-4 py-3 font-mono text-sm text-terminal-text placeholder-terminal-muted/50 outline-none transition-all duration-200 focus:border-terminal-green ${errors.email ? 'border-red-600' : 'border-terminal-border'}`}
          {...register('email', {
            required: 'Email is required',
            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
          })}
        />
        {errors.email && <p className="font-mono text-xs text-red-400 mt-1">{errors.email.message}</p>}
      </div>
      <button
        type="submit"
        disabled={isLoading}
        className="w-full bg-terminal-green text-black font-mono font-bold text-sm tracking-widest uppercase rounded-lg py-3 transition-all duration-150 hover:bg-terminal-green-bright active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {isLoading ? (
          <><span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" /><span>SENDING...</span></>
        ) : 'SEND RESET LINK'}
      </button>
      <button type="button" onClick={onBack} className="mt-3 w-full font-mono text-xs text-terminal-muted hover:text-terminal-green transition-colors">
        ← Back to login
      </button>
    </form>
  );
}

export default function LoginForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [showReset, setShowReset] = useState(false);
  const router = useRouter();
  const { signIn } = useAuth();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginFormData>();

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setAuthError(null);
    try {
      await signIn(data.email.trim(), data.password);
      toast.success('ACCESS GRANTED — Welcome back, hunter.', { icon: '🟢' });
      router.push('/admin-command-center');
      router.refresh();
    } catch (err: any) {
      setAuthError(
        err?.message === 'Invalid login credentials' ?'Invalid credentials — check your email and password' : err?.message ||'Authentication failed — please try again'
      );
      setIsLoading(false);
    }
  };

  const handleAutofill = (email: string, password: string) => {
    setValue('email', email);
    setValue('password', password);
    toast('Credentials loaded. Hit INITIATE.', { icon: '⚡' });
  };

  if (showReset) {
    return <ForgotPasswordForm onBack={() => setShowReset(false)} />;
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="bg-terminal-card border border-terminal-border rounded-xl p-6 terminal-glow"
    >
      {authError && (
        <div className="flex items-start gap-2 mb-5 bg-red-950/40 border border-red-800/50 rounded-lg px-4 py-3 fade-in">
          <AlertTriangle size={14} className="text-red-400 mt-0.5 shrink-0" />
          <p className="font-mono text-xs text-red-400 leading-relaxed">{authError}</p>
        </div>
      )}

      {/* Email */}
      <div className="mb-4">
        <label
          htmlFor="email"
          className="block font-mono text-xs text-terminal-muted uppercase tracking-widest mb-1.5"
        >
          EMAIL ADDRESS
        </label>
        <input
          id="email"
          type="email"
          autoComplete="email"
          placeholder="hunter@bloodhound.run"
          className={`w-full bg-terminal-bg border rounded-lg px-4 py-3 font-mono text-sm text-terminal-text placeholder-terminal-muted/50 outline-none transition-all duration-200 focus:border-terminal-green focus:terminal-glow-sm ${
            errors.email ? 'border-red-600' : 'border-terminal-border'
          }`}
          {...register('email', {
            required: 'Email is required',
            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
          })}
        />
        {errors.email && (
          <p className="font-mono text-xs text-red-400 mt-1">{errors.email.message}</p>
        )}
      </div>

      {/* Password */}
      <div className="mb-2">
        <label
          htmlFor="password"
          className="block font-mono text-xs text-terminal-muted uppercase tracking-widest mb-1.5"
        >
          PASSWORD
        </label>
        <div className="relative">
          <input
            id="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            placeholder="••••••••••••••••"
            className={`w-full bg-terminal-bg border rounded-lg px-4 py-3 pr-12 font-mono text-sm text-terminal-text placeholder-terminal-muted/50 outline-none transition-all duration-200 focus:border-terminal-green focus:terminal-glow-sm ${
              errors.password ? 'border-red-600' : 'border-terminal-border'
            }`}
            {...register('password', { required: 'Password is required' })}
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-terminal-muted hover:text-terminal-green transition-colors duration-150"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        {errors.password && (
          <p className="font-mono text-xs text-red-400 mt-1">{errors.password.message}</p>
        )}
      </div>

      {/* Forgot password */}
      <div className="flex justify-end mb-5">
        <button
          type="button"
          onClick={() => setShowReset(true)}
          className="font-mono text-xs text-terminal-muted hover:text-terminal-green transition-colors"
        >
          Forgot password?
        </button>
      </div>

      <button
        type="submit"
        disabled={isLoading}
        className="w-full bg-terminal-green text-black font-mono font-bold text-sm tracking-widest uppercase rounded-lg py-3 transition-all duration-150 hover:bg-terminal-green-bright active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        style={{ minHeight: '48px' }}
      >
        {isLoading ? (
          <>
            <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
            <span>AUTHENTICATING...</span>
          </>
        ) : (
          'INITIATE SESSION'
        )}
      </button>

      <CredentialBox onUse={handleAutofill} />
    </form>
  );
}